import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import CircleMessageGroup from './circle_message_group';
import EventMessageGroup from './event_message_group';
import MessageGroup from './message_group';
import Loader from '../loader';
import useInboxChannel from '../../hooks/useInboxChannel';

// 未読メッセージ一覧コンポーネント
//
// TGW-1033: 周期ポーリングを撤廃し、MessageInboxChannel 経由の WebSocket で
// リアルタイム更新する。既読化（unread_count_changed）で 0 になった項目は
// 一覧から除外する。
function UnreadMessageGroupList(props) {
  const { companyId } = props;

  const initialData = {
    unreadCircles: [],
    unreadEvents: [],
    unreadMessageGroups: [],
    unreadCircleMessageCount: {},
    unreadEventMessageCount: {},
    unreadDirectMessageCount: {},
    lastCircleMessages: {},
    lastEventMessages: {},
    lastDirectMessages: {},
    currentUserId: 0,
  };

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(initialData);

  const fetchUnreads = useCallback(() => {
    return new Promise((resolve) => {
      const cid = typeof companyId === 'undefined' ? null : companyId;
      $.ajax({
        url: '/api/message/unread_messages',
        data: { company_id: cid },
        dataType: 'json',
        cache: false,
        success: (resp) => {
          setLoading(false);
          setData((prev) => ({
            ...prev,
            ...resp,
            unreadCircleMessageCount: resp.unreadCircleMessageCount || {},
            unreadEventMessageCount: resp.unreadEventMessageCount || {},
            unreadDirectMessageCount: resp.unreadDirectMessageCount || {},
            lastCircleMessages: resp.lastCircleMessages || {},
            lastEventMessages: resp.lastEventMessages || {},
            lastDirectMessages: resp.lastDirectMessages || {},
          }));
          resolve();
        },
        error: () => {
          resolve();
        },
      });
    });
  }, [companyId]);

  useEffect(() => {
    fetchUnreads();
  }, [fetchUnreads]);

  // 新着受信時: kind に応じて該当コレクションの先頭に並べ替え + バッジ更新
  // 一覧に存在しない target なら次回 fetch で吸収する（ここでは追加しない）
  const onMessageReceived = useCallback((payload) => {
    setData((prev) => {
      const targetId = payload.target_id;
      const isSelfSend = (payload.unread_count_delta || 0) === 0;

      const updateCollection = (
        list,
        countMap,
        lastMap,
        countKey,
        lastKey,
        listKey,
      ) => {
        const idx = list.findIndex((item) => item.id === targetId);
        if (idx < 0) return null;
        const item = list[idx];
        const reordered = [item, ...list.slice(0, idx), ...list.slice(idx + 1)];
        const prevUnread = (countMap || {})[targetId] || 0;
        const nextUnread = isSelfSend
          ? prevUnread
          : prevUnread + (payload.unread_count_delta || 1);
        const prevLast = (lastMap || {})[targetId] || {};
        const nextLast = {
          ...prevLast,
          body: payload.last_message_preview,
          created_at: payload.last_message_at,
          sender_id: payload.last_sender_id,
        };
        return {
          [listKey]: reordered,
          [countKey]: { ...(countMap || {}), [targetId]: nextUnread },
          [lastKey]: { ...(lastMap || {}), [targetId]: nextLast },
        };
      };

      let patch = null;
      if (payload.message_group_kind === 'room') {
        patch = updateCollection(
          prev.unreadMessageGroups,
          prev.unreadDirectMessageCount,
          prev.lastDirectMessages,
          'unreadDirectMessageCount',
          'lastDirectMessages',
          'unreadMessageGroups',
        );
      } else if (payload.message_group_kind === 'circle') {
        patch = updateCollection(
          prev.unreadCircles,
          prev.unreadCircleMessageCount,
          prev.lastCircleMessages,
          'unreadCircleMessageCount',
          'lastCircleMessages',
          'unreadCircles',
        );
      } else if (payload.message_group_kind === 'event') {
        patch = updateCollection(
          prev.unreadEvents,
          prev.unreadEventMessageCount,
          prev.lastEventMessages,
          'unreadEventMessageCount',
          'lastEventMessages',
          'unreadEvents',
        );
      }

      if (!patch) return prev;
      return { ...prev, ...patch };
    });
  }, []);

  // F4: 既読化 → 該当 kind の一覧から除外（unread_count===0）
  const onUnreadCountChanged = useCallback((payload) => {
    setData((prev) => {
      const targetId = payload.target_id;
      const newCount = payload.unread_count || 0;

      const dropFromCollection = (list, countMap, lastMap) => {
        if (newCount > 0) {
          // 0 でなければバッジだけ更新
          const idx = list.findIndex((item) => item.id === targetId);
          if (idx < 0) return null;
          return {
            countMap: { ...(countMap || {}), [targetId]: newCount },
          };
        }
        // 0 になった → 一覧から削除
        const filteredList = list.filter((item) => item.id !== targetId);
        if (filteredList.length === list.length) return null;
        const newCountMap = { ...(countMap || {}) };
        delete newCountMap[targetId];
        const newLastMap = { ...(lastMap || {}) };
        delete newLastMap[targetId];
        return {
          list: filteredList,
          countMap: newCountMap,
          lastMap: newLastMap,
        };
      };

      if (payload.message_group_kind === 'room') {
        const result = dropFromCollection(
          prev.unreadMessageGroups,
          prev.unreadDirectMessageCount,
          prev.lastDirectMessages,
        );
        if (!result) return prev;
        const next = { ...prev };
        if (result.list) next.unreadMessageGroups = result.list;
        if (result.countMap) next.unreadDirectMessageCount = result.countMap;
        if (result.lastMap) next.lastDirectMessages = result.lastMap;
        return next;
      }
      if (payload.message_group_kind === 'circle') {
        const result = dropFromCollection(
          prev.unreadCircles,
          prev.unreadCircleMessageCount,
          prev.lastCircleMessages,
        );
        if (!result) return prev;
        const next = { ...prev };
        if (result.list) next.unreadCircles = result.list;
        if (result.countMap) next.unreadCircleMessageCount = result.countMap;
        if (result.lastMap) next.lastCircleMessages = result.lastMap;
        return next;
      }
      if (payload.message_group_kind === 'event') {
        const result = dropFromCollection(
          prev.unreadEvents,
          prev.unreadEventMessageCount,
          prev.lastEventMessages,
        );
        if (!result) return prev;
        const next = { ...prev };
        if (result.list) next.unreadEvents = result.list;
        if (result.countMap) next.unreadEventMessageCount = result.countMap;
        if (result.lastMap) next.lastEventMessages = result.lastMap;
        return next;
      }
      return prev;
    });
  }, []);

  // F6: 新規スレッドは一旦無視し、refetch で正規化（未読画面は厳密な集計が必要なため）
  const onNewThread = useCallback(() => {
    fetchUnreads();
  }, [fetchUnreads]);

  useInboxChannel({
    currentUserId: data.currentUserId,
    companyId: typeof companyId === 'undefined' ? null : companyId,
    scope: 'unread',
    onMessageReceived,
    onUnreadCountChanged,
    onNewThread,
    refetch: fetchUnreads,
  });

  const lastCircle = data.unreadCircles[data.unreadCircles.length - 1];
  const lastEvent = data.unreadEvents[data.unreadEvents.length - 1];
  const lastGroup =
    data.unreadMessageGroups[data.unreadMessageGroups.length - 1];
  const eventCount = data.unreadEvents.length;
  const groupCount = data.unreadMessageGroups.length;
  const currentUserId = data.currentUserId;

  const unreadCircles = data.unreadCircles.map((circle) => {
    return (
      <CircleMessageGroup
        circle={circle}
        currentCircleId={0}
        hasUnreadMessage={true}
        key={circle.id}
        isLast={eventCount + groupCount === 0 && lastCircle === circle}
        unreadMessageCount={(data.unreadCircleMessageCount || {})[circle.id]}
        lastMessage={(data.lastCircleMessages || {})[circle.id]}
      />
    );
  });

  const unreadEvents = data.unreadEvents.map((event) => {
    return (
      <EventMessageGroup
        event={event}
        currentEventId={0}
        hasUnreadMessage={true}
        key={event.id}
        isLast={groupCount === 0 && lastEvent === event}
        unreadMessageCount={(data.unreadEventMessageCount || {})[event.id]}
        lastMessage={(data.lastEventMessages || {})[event.id]}
      />
    );
  });

  const unreadDirectMessageGroups = data.unreadMessageGroups.map((group) => {
    return (
      <MessageGroup
        messageGroup={group}
        currentUserId={currentUserId}
        messageGroupId={0}
        hasUnreadMessage={true}
        key={group.id}
        isLast={lastGroup === group}
        unreadMessageCount={(data.unreadDirectMessageCount || {})[group.id]}
        lastMessage={(data.lastDirectMessages || {})[group.id]}
      />
    );
  });

  const hasAnyUnread =
    data.unreadMessageGroups.length !== 0 ||
    data.unreadCircles.length !== 0 ||
    data.unreadEvents.length !== 0;

  const unreads = hasAnyUnread ? (
    <div>
      <div className="container col-md-8 col-lg-6">
        <h6 className="h6--list-title text-fz-14px text-black-50 pt-2">
          未読のメッセージ
        </h6>
      </div>
      <div className="bg-white h-100">
        <div className="container col-md-8 col-lg-6 h-100">
          <Loader isActive={loading} />
          <ul className="list-unstyled mb-0">
            {unreadCircles}
            {unreadEvents}
            {unreadDirectMessageGroups}
          </ul>
        </div>
      </div>
    </div>
  ) : (
    ''
  );

  return <div>{unreads}</div>;
}

UnreadMessageGroupList.displayName = 'Root';
UnreadMessageGroupList.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default UnreadMessageGroupList;
